import { DEFAULT_SCREEN_SETTINGS } from '@/constants'
import {
  REVIEW_FLOW_STEPS,
  WidgetChatScreen,
  WidgetFlow,
  WidgetPriceScreen,
  WidgetQuestionField,
  WidgetQuestionFields,
  WidgetRecFlowScreen,
  WidgetReviewFlowScreen,
  WidgetReviewMyReviewsScreenSettings,
  WidgetReviewQuestionField,
  WidgetReviewQuestionFields,
  WidgetSettings,
  WidgetStep,
  WidgetTerpenesScreen,
  type WidgetScreen,
  type WidgetSignInScreen,
} from '@/types'
import { isIOS } from '@/utils/device'
import { useFlowState } from './stores/useFlowState'
import { getFilters, isEdiblesFlowSelected, useRecommendationStore } from './stores/useRecommendationStore'
import {
  getCurrentGlobalID,
  getCurrentMenuType,
  getWidgetSettings,
  isRestrictedFlowEnabled,
  isReviewAuthenticated,
  needsMenuTypeSelection,
} from './stores/useSessionStore'
import { useRewardsStore } from './stores/useRewardsStore'
import { getReviewForm, useSubmitReviewStore } from './stores/useSubmitReviewStore'
import {
  handleCodeRequestAndVerify as handleAuthSignIn,
  handleResendCode,
  hasSentCode,
  useWidgetSignInFormStore,
} from './stores/useWidgetSignInFormStore'
import { EdibleFilters, InhalableFilters, ScentsFilters } from './types/widget'
import { IS_DEVELOPMENT } from './components/screen/constants'

// =============================================================================
// Types
// =============================================================================

type ButtonConfig = {
  label: string
  onClick?: () => void | Promise<void>
  disabled?: () => boolean
  loading?: () => boolean
}

type GetButtonConfig = () => ButtonConfig | undefined

export type WidgetStepSettings<T extends WidgetScreen = WidgetScreen> = (
  widgetSettings?: WidgetSettings
) => T | undefined

export type WidgetStepConfig<T extends WidgetScreen = WidgetScreen> = {
  flow: WidgetFlow | undefined
  backPlacement?: 'replace-hamburger-menu' | 'default'
  cannotGoBack?: () => boolean
  showNextButton?: () => boolean
  isComplete: () => boolean
  isRequired?: () => boolean
  showStepsIndicator?: () => boolean
  primaryButton?: GetButtonConfig
  secondaryButton?: GetButtonConfig
  skipButton?: GetButtonConfig
  getSettings: WidgetStepSettings<T>
}

// =============================================================================
// Settings Helpers
// =============================================================================

/**
 * Deep merges user settings with defaults.
 * - Creates a fresh copy to avoid mutating DEFAULT_SCREEN_SETTINGS
 * - Respects explicit false/0 values from user settings
 * - Treats null and undefined as "use default"
 */
function mergeSettings<T extends WidgetScreen>(defaultSettings: T, settings: T | undefined): T {
  if (!settings) {
    return { ...defaultSettings }
  }

  const result = { ...defaultSettings } as T

  for (const key of Object.keys(settings) as (keyof T)[]) {
    const userValue = settings[key]
    const defaultValue = defaultSettings[key]

    // Skip null and undefined - use default value
    if (userValue === undefined || userValue === null) {
      continue
    }

    // Deep merge nested objects (but not arrays)
    if (
      typeof userValue === 'object' &&
      !Array.isArray(userValue) &&
      typeof defaultValue === 'object' &&
      defaultValue !== null &&
      !Array.isArray(defaultValue)
    ) {
      result[key] = mergeSettings(defaultValue as WidgetScreen, userValue as WidgetScreen) as T[keyof T]
    } else {
      result[key] = userValue
    }
  }

  return result
}

function resolveChatScreenSettings(tenant?: WidgetChatScreen): WidgetChatScreen {
  const defaults = DEFAULT_SCREEN_SETTINGS.chat
  const merged = mergeSettings(defaults, tenant)
  const tenantHeader = tenant?.headerText?.trim()
  const tenantAssistant = tenant?.assistantName?.trim()
  const hasCustomAssistant =
    Boolean(tenantAssistant) && tenantAssistant !== defaults.assistantName

  if (hasCustomAssistant && !tenantHeader) {
    return { ...merged, headerText: tenantAssistant, title: tenantAssistant }
  }

  return merged
}

// =============================================================================
// Button Factory Functions
// =============================================================================

const createNextButton = (step: WidgetStep): GetButtonConfig => {
  return () => ({
    label: 'Next',
    disabled: () => !WidgetStepConfigs[step].isComplete(),
  })
}

const createSkipButtonIfOptional = (step: WidgetStep): GetButtonConfig => {
  return () => {
    const stepConfig = WidgetStepConfigs[step]
    const isRequired = stepConfig.isRequired?.()
    const isComplete = stepConfig.isComplete()
    if (isRequired || isComplete) {
      return undefined
    }
    return { label: 'Skip' }
  }
}

const createSignInButtons = (): {
  primaryButton: GetButtonConfig
  secondaryButton: GetButtonConfig
  skipButton: GetButtonConfig
} => ({
  primaryButton: () => ({
    label: 'Sign in',
    onClick: () => {
      if (useWidgetSignInFormStore.getState().loading) return
      return handleAuthSignIn()
    },
    disabled: () => useWidgetSignInFormStore.getState().loading,
  }),
  secondaryButton: () => {
    if (!hasSentCode()) return undefined
    return {
      label: 'Resend',
      onClick: () => {
        if (useWidgetSignInFormStore.getState().loading) return
        return handleResendCode()
      },
      disabled: () => useWidgetSignInFormStore.getState().loading,
    }
  },
  skipButton: () => ({ label: 'Skip' }),
})

const signInButtons = createSignInButtons()

// =============================================================================
// Flow Steps Logic
// =============================================================================

export function getActiveFlowSteps(flow?: WidgetFlow): WidgetStep[] {
  if (!flow) return []
  switch (flow) {
    case WidgetFlow.Recommendation:
      return getRecommendationFlowStepsInOrder()
    case WidgetFlow.ProductReview:
      return getReviewFlowStepsInOrder()
    case WidgetFlow.MyReviews:
      return getMyReviewsSteps()
    case WidgetFlow.Chat:
      return getChatFlowSteps()
    default:
      return []
  }
}

/**
 * Gets the flow steps in order for the chat flow.
 * Chat is a single-step "flow" — no gating on store selection so users can
 * ask questions before/without picking a store. The astro backend accepts an
 * optional storeID which we still pass when known.
 */
function getChatFlowSteps(): WidgetStep[] {
  return [WidgetStep.Chat]
}

/**
 * All possible recommendation flow question steps (before filtering)
 */
const REC_FLOW_INHALABLE_STEPS: WidgetStep[] = [
  WidgetStep.Category,
  WidgetStep.Effects,
  WidgetStep.Potency,
  WidgetStep.Terpenes,
  WidgetStep.Price,
]

const REC_FLOW_EDIBLE_STEPS: WidgetStep[] = [
  WidgetStep.Category,
  WidgetStep.SubCategory,
  WidgetStep.Effects,
  WidgetStep.Potency,
]

/**
 * Checks if a recommendation flow step is required based on settings.
 * For the edibles flow, prefers `edibleRequired` from settings, then the
 * metadata's `edibleRequired` default. For inhalables, uses `required` and
 * the metadata's `required` default.
 */
function isRecStepRequired(step: WidgetQuestionField): boolean {
  const settings = WidgetStepConfigs[step]?.getSettings() as WidgetRecFlowScreen | undefined
  const metaData = WidgetQuestionFields[step]

  if (isEdiblesFlowSelected()) {
    const edibleRequired = settings?.edibleRequired
    if (edibleRequired !== undefined && edibleRequired !== null) {
      return edibleRequired
    }
    return metaData?.edibleRequired ?? metaData?.required ?? false
  }

  const required = settings?.required
  if (required !== undefined && required !== null) {
    return required
  }

  return metaData?.required ?? false
}

/**
 * Checks if a recommendation flow step is enabled based on settings
 */
function isRecStepEnabled(step: WidgetStep, isEdiblesFlow: boolean): boolean {
  const restricted = isRestrictedFlowEnabled()

  if (restricted && step === WidgetStep.Effects) return false

  if (restricted && isEdiblesFlow) {
    return step === WidgetStep.SubCategory || step === WidgetStep.Potency
  }

  // Category is always enabled
  if (step === WidgetStep.Category) return true
  // SubCategory is only for edibles and always enabled when in edibles flow
  if (step === WidgetStep.SubCategory) return isEdiblesFlow

  const settings = WidgetStepConfigs[step]?.getSettings() as WidgetRecFlowScreen | undefined
  const metaData = WidgetQuestionFields[step as WidgetQuestionField]

  if (isEdiblesFlow) {
    const edibleEnabled = settings?.edibleEnabled
    if (edibleEnabled !== undefined && edibleEnabled !== null) {
      return edibleEnabled
    }
    return metaData?.edibleEnabled ?? metaData?.enabled ?? true
  }

  const enabled = settings?.enabled
  if (enabled !== undefined && enabled !== null) {
    return enabled
  }

  return metaData?.enabled ?? true
}

export function getRecommendedQuestionSteps(ediblesFlow: boolean = isEdiblesFlowSelected()): WidgetStep[] {
  const allSteps = ediblesFlow ? REC_FLOW_EDIBLE_STEPS : REC_FLOW_INHALABLE_STEPS

  return allSteps
    .filter((step) => isRecStepEnabled(step, ediblesFlow))
    .sort((a, b) => getRecommendationFlowQuestionPriority(a) - getRecommendationFlowQuestionPriority(b))
}

/**
 * Inserts a step into the flow if the condition is true
 * @param step - The step to insert
 * @param condition - The condition to check
 * @returns The flow steps
 */
const insertStep = (step: WidgetStep, condition: boolean): WidgetStep[] => (condition ? [step] : []) // If the condition is true, insert the step into the flow

/**
 * Gets the flow steps in order for the recommendation flow
 * @returns The flow steps
 */
function getRecommendationFlowStepsInOrder(): WidgetStep[] {
  const questionsSteps = getRecommendedQuestionSteps()
  const questions = questionsSteps.map((step) => ({
    step,
    priority: getRecommendationFlowQuestionPriority(step),
  }))

  const globalID = getCurrentGlobalID()
  const menuType = getCurrentMenuType()

  const { recReward, loading: rewardsLoading } = useRewardsStore.getState()
  const recEligible = rewardsLoading || (!recReward ? true : recReward.userEligible && (recReward.points ?? 0) > 0)

  return [
    ...insertStep(WidgetStep.SelectStore, !globalID),
    ...insertStep(WidgetStep.SelectMenuType, !!globalID && !menuType && needsMenuTypeSelection()),
    ...questions.map((q) => q.step),
    ...insertStep(WidgetStep.RecSignInPage, recEligible),
    ...insertStep(WidgetStep.WalletPass, false),
    WidgetStep.Recommendations,
  ]
}

/**
 * Gets the flow steps in order for the review flow
 * Uses priority settings to determine order, filters disabled steps
 * @returns The flow steps
 */
function getReviewFlowStepsInOrder(): WidgetStep[] {
  const loyaltySettings = getWidgetSettings()?.loyaltySettings ?? {}
  const hasEnabledLoyalty = (loyaltySettings.enabled ?? false) || IS_DEVELOPMENT

  // Prefer the new `reviewRewards.pointsPerReview.enabled` shape (widgetapi
  // PR #8223). Fall back to the legacy `reviewPointsGift.enabled` so merchant
  // settings that haven't migrated yet still gate the ClaimPoints step
  // correctly.
  const hasReviewRewards =
    !!loyaltySettings.reviewRewards?.pointsPerReview?.enabled ||
    !!loyaltySettings.reviewPointsGift?.enabled
  const hasClaimPoints = hasEnabledLoyalty && hasReviewRewards

  const questionSteps = getReviewQuestionSteps()

  return [
    ...questionSteps,
    ...insertStep(WidgetStep.ReviewClaimPoints, hasClaimPoints),
    WidgetStep.ReviewComplete,
  ]
}

/**
 * Gets the flow steps in order for My reviews (pre-review flow)
 * @returns The flow steps
 */
function getMyReviewsSteps(): WidgetStep[] {
  const globalID = getCurrentGlobalID()
  const menuType = getCurrentMenuType()

  return [
    ...insertStep(WidgetStep.SelectStore, !globalID),
    ...insertStep(WidgetStep.SelectMenuType, !!globalID && !menuType && needsMenuTypeSelection()),
    ...insertStep(WidgetStep.ReviewSignInPage, !isReviewAuthenticated()),
    WidgetStep.ReviewMyReviews,
  ]
}

/**
 * Gets the priority of a question step in the recommendation flow
 * @param step - The step to get the priority of
 * @returns The priority of the step
 */
export const getRecommendationFlowQuestionPriority = (step: WidgetStep): number => {
  if (step === WidgetStep.Category) {
    return 0
  }

  const isEdiblesFlow = isEdiblesFlowSelected()
  const settings = WidgetStepConfigs[step].getSettings() as WidgetRecFlowScreen
  const priority = settings?.priority ?? 0

  return isEdiblesFlow ? (settings?.ediblePriority ?? priority) : priority
}

/**
 * Gets the priority of a question step in the review flow
 * @param step - The step to get the priority of
 * @returns The priority of the step
 */
export const getReviewFlowQuestionPriority = (step: WidgetStep): number => {
  const metaData = WidgetReviewQuestionFields[step as WidgetReviewQuestionField]
  if (!metaData) return 999

  const settings = WidgetStepConfigs[step].getSettings() as WidgetReviewFlowScreen
  return settings?.priority ?? metaData.defaultPriority
}

/**
 * Gets the review question steps filtered by enabled and sorted by priority
 * @returns The ordered review question steps
 */
export function getReviewQuestionSteps(): WidgetStep[] {
  return REVIEW_FLOW_STEPS.filter((step) => {
    const metaData = WidgetReviewQuestionFields[step]
    const settings = WidgetStepConfigs[step].getSettings() as WidgetReviewFlowScreen
    return settings?.enabled ?? metaData.defaultEnabled
  }).sort((a, b) => getReviewFlowQuestionPriority(a) - getReviewFlowQuestionPriority(b))
}

// =============================================================================
// Step Configs - Global Steps
// =============================================================================

const GlobalStepConfigs: Partial<Record<WidgetStep, WidgetStepConfig>> = {
  [WidgetStep.SignInPage]: {
    flow: undefined,
    isComplete: () => true,
    isRequired: () => true,
    getSettings<T extends WidgetSignInScreen>(): T | undefined {
      return mergeSettings<WidgetSignInScreen>(
        DEFAULT_SCREEN_SETTINGS.signIn,
        getWidgetSettings()?.screenSettings?.signIn
      ) as T
    },
    primaryButton: signInButtons.primaryButton,
    secondaryButton: signInButtons.secondaryButton,
  },

  [WidgetStep.Welcome]: {
    flow: undefined,
    cannotGoBack: () => true,
    isComplete: () => true,
    getSettings<T extends WidgetScreen>(): T | undefined {
      return mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.welcome,
        getWidgetSettings()?.screenSettings?.welcome
      ) as T
    },
  },

  [WidgetStep.HowItWorks]: {
    flow: undefined,
    isComplete: () => true,
    isRequired: () => true,
    getSettings<T extends WidgetScreen>(): T | undefined {
      return mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.howItWorks,
        getWidgetSettings()?.screenSettings?.howItWorks
      ) as T
    },
  },
  [WidgetStep.AboutTerpenes]: {
    flow: undefined,
    isComplete: () => true,
    isRequired: () => true,
    getSettings<T extends WidgetScreen>(): T | undefined {
      return mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.aboutTerpenes,
        getWidgetSettings()?.screenSettings?.aboutTerpenes
      ) as T
    },
  },
  [WidgetStep.ProductDetails]: {
    flow: undefined,
    isComplete: () => true,
    isRequired: () => true,
    backPlacement: 'replace-hamburger-menu',
    getSettings<T extends WidgetScreen>(): T | undefined {
      return mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.productDetails,
        getWidgetSettings()?.screenSettings?.productDetails
      ) as T
    },
  },
}

// =============================================================================
// Step Configs - Recommendation Flow
// =============================================================================

const RecommendationFlowStepConfigs: Partial<Record<WidgetStep, WidgetStepConfig>> = {
  [WidgetStep.SelectStore]: {
    flow: WidgetFlow.Recommendation,
    showNextButton: () => {
      const currentFlow = useFlowState.getState().currentFlow
      const hasFlow = currentFlow === WidgetFlow.Recommendation || currentFlow === WidgetFlow.ProductReview
      return getCurrentGlobalID() !== undefined && hasFlow
    },
    isComplete: () => getCurrentGlobalID() !== undefined,
    isRequired: () => useFlowState.getState().currentFlow === WidgetFlow.Recommendation,
    getSettings<T extends WidgetScreen>(): T | undefined {
      return mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.selectStore,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.selectStore
      ) as T
    },
  },

  [WidgetStep.SelectMenuType]: {
    flow: WidgetFlow.Recommendation,
    showNextButton: () => {
      const currentFlow = useFlowState.getState().currentFlow
      const hasFlow = currentFlow === WidgetFlow.Recommendation || currentFlow === WidgetFlow.ProductReview
      return getCurrentMenuType() !== undefined && hasFlow
    },
    isComplete: () => getCurrentMenuType() !== undefined,
    isRequired: () => true,
    getSettings<T extends WidgetScreen>(): T | undefined {
      return mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.selectMenuType,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.selectMenuType
      ) as T
    },
  },

  [WidgetStep.Category]: {
    flow: WidgetFlow.Recommendation,
    showStepsIndicator: () => true,
    isComplete: () => (getFilters()?.category?.length ?? 0) > 0,
    isRequired: () => true,
    getSettings<T extends WidgetRecFlowScreen>(): T | undefined {
      return mergeSettings<WidgetRecFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.recCategory,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.category
      ) as T
    },
    primaryButton: () => ({
      label: 'Next',
      onClick: () => useFlowState.getState().goForward(),
      disabled: () => !WidgetStepConfigs[WidgetStep.Category].isComplete(),
    }),
  },

  [WidgetStep.SubCategory]: {
    flow: WidgetFlow.Recommendation,
    showStepsIndicator: () => true,
    isComplete: () => (getFilters<EdibleFilters>()?.edibleCategory?.length ?? 0) > 0,
    isRequired: () => true,
    getSettings<T extends WidgetRecFlowScreen>(): T | undefined {
      const settings = mergeSettings<WidgetRecFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.recSubCategory,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.subCategory
      ) as T
      const selectedCategory = getFilters()?.category?.[0]
      settings.title = settings.title?.replace('{category}', (selectedCategory ?? '').toLowerCase())
      return settings
    },
    primaryButton: createNextButton(WidgetStep.SubCategory),
  },

  [WidgetStep.Effects]: {
    flow: WidgetFlow.Recommendation,
    showStepsIndicator: () => true,
    isComplete: () => {
      if (isEdiblesFlowSelected()) {
        return (getFilters<EdibleFilters>()?.basicEffect?.length ?? 0) > 0
      }
      return (getFilters<InhalableFilters>()?.effects?.length ?? 0) > 0
    },
    isRequired: () => isRecStepRequired(WidgetStep.Effects),
    getSettings<T extends WidgetRecFlowScreen>(): T | undefined {
      return mergeSettings<WidgetRecFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.recEffects,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.effects
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.Effects),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.Effects),
  },

  [WidgetStep.Potency]: {
    flow: WidgetFlow.Recommendation,
    showStepsIndicator: () => true,
    isComplete: () => {
      if (isEdiblesFlowSelected()) {
        return (getFilters<EdibleFilters>()?.dosage?.length ?? 0) > 0
      }
      return getFilters<InhalableFilters>()?.strength !== undefined
    },
    isRequired: () => isRecStepRequired(WidgetStep.Potency),
    getSettings<T extends WidgetRecFlowScreen>(): T | undefined {
      return mergeSettings<WidgetRecFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.recPotency,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.potency
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.Potency),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.Potency),
  },

  [WidgetStep.Terpenes]: {
    flow: WidgetFlow.Recommendation,
    showStepsIndicator: () => true,
    isComplete: () => {
      const filters = getFilters<InhalableFilters & ScentsFilters>()
      return (filters?.terpenes?.length ?? 0) > 0 || !!filters?.scent
    },
    isRequired: () => isRecStepRequired(WidgetStep.Terpenes),
    getSettings<T extends WidgetTerpenesScreen>(): T | undefined {
      const widgetSettings = getWidgetSettings()
      const screenSettings = widgetSettings?.recommendationSettings?.screenSettings
      const terpeneSettings = screenSettings?.terpene as WidgetTerpenesScreen
      const maxTerpenes = terpeneSettings?.terpenesRange?.max ?? 3

      const isRestricted = isRestrictedFlowEnabled(widgetSettings)
      const title = isRestricted ? 'Select a terpene' : `Select up to ${maxTerpenes} terpenes`

      return mergeSettings<WidgetTerpenesScreen>(
        { ...DEFAULT_SCREEN_SETTINGS.recTerpenes, title },
        screenSettings?.terpene
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.Terpenes),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.Terpenes),
  },

  [WidgetStep.Price]: {
    flow: WidgetFlow.Recommendation,
    showStepsIndicator: () => true,
    isComplete: () => {
      const price = getFilters<InhalableFilters>()?.price
      return price?.min !== undefined && price?.max !== undefined
    },
    isRequired: () => isRecStepRequired(WidgetStep.Price),
    getSettings<T extends WidgetPriceScreen>(): T | undefined {
      return mergeSettings<WidgetPriceScreen>(
        DEFAULT_SCREEN_SETTINGS.recPrice,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.price
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.Price),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.Price),
  },

  [WidgetStep.WalletPass]: {
    flow: WidgetFlow.Recommendation,
    isComplete: () => true,
    isRequired: () => false,
    getSettings<T extends WidgetScreen>(): T | undefined {
      const isApple = isIOS()
      const settings = mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.walletPass,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.walletPass
      ) as T
      settings.title = settings.title?.replace('{platform}', isApple ? 'Apple' : 'Google')
      return settings
    },
    skipButton: () => ({ label: 'Skip' }),
  },

  [WidgetStep.Recommendations]: {
    flow: WidgetFlow.Recommendation,
    isComplete: () => true,
    isRequired: () => false,
    getSettings<T extends WidgetScreen>(): T | undefined {
      const settings = mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.recommendations,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.recommendations
      ) as T

      const category = getFilters()?.category?.join(', ')
      const effect = getFilters<InhalableFilters>()
        ?.effects?.join(', ')
        .replace(/,([^,]*)$/, ' and$1')

      settings.description = settings.description?.replace(
        '{category}',
        (category ?? 'something').toLowerCase()
      )
      settings.description = settings.description?.replace('{effect}', (effect ?? 'any').toLowerCase())

      return settings
    },
    primaryButton: () => ({
      label: 'Get another recommendation',
      loading() {
        return useRecommendationStore.getState().loading
      },
      onClick: () => {
        useRecommendationStore.getState().resetRequest()
        useFlowState.getState().setCurrentStep(WidgetStep.Category)
      },
    }),
  },

  [WidgetStep.RecSignInPage]: {
    flow: WidgetFlow.Recommendation,
    isComplete: () => true,
    isRequired: () => false,
    getSettings<T extends WidgetSignInScreen>(): T | undefined {
      const settings = mergeSettings<WidgetSignInScreen>(
        DEFAULT_SCREEN_SETTINGS.recSignIn,
        getWidgetSettings()?.recommendationSettings?.screenSettings?.signIn
      ) as T

      const recReward = useRewardsStore.getState().recReward
      const hasPoints = recReward?.userEligible && (recReward?.points ?? 0) > 0

      if (hasPoints) {
        settings.title = settings.title?.replace('{points}', String(recReward.points))
      } else {
        settings.title =
          (settings as WidgetSignInScreen).noPointsTitle ?? settings.title?.replace('{points}', '0')
      }

      return settings
    },
    primaryButton: () => ({
      label: 'Claim Points',
      onClick: () => useRewardsStore.getState().claimRecReward(),
      loading: () => useRewardsStore.getState().claiming,
    }),
    skipButton: () => ({ label: 'Skip' }),
  },
}

const cancelReviewFlow = () => {
  useFlowState.getState().setCurrentFlow(WidgetFlow.MyReviews)
  useSubmitReviewStore.getState().reset()
}

const createCancelReviewButton = (): GetButtonConfig => {
  return () => ({
    label: 'Cancel',
    onClick: () => {
      cancelReviewFlow()
    },
  })
}

// =============================================================================
// Step Configs - Review Flow
// =============================================================================

const ReviewFlowStepConfigs: Partial<Record<WidgetStep, WidgetStepConfig>> = {
  [WidgetStep.ReviewSignInPage]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => true,
    isRequired: () => true,
    getSettings<T extends WidgetSignInScreen>(): T | undefined {
      return mergeSettings<WidgetSignInScreen>(
        DEFAULT_SCREEN_SETTINGS.reviewSignIn,
        getWidgetSettings()?.reviewSettings?.screenSettings?.signIn
      ) as T
    },
    primaryButton: signInButtons.primaryButton,
    secondaryButton: signInButtons.secondaryButton,
  },

  [WidgetStep.ReviewStars]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => getReviewForm()?.rating !== undefined,
    isRequired: () => {
      const settings = WidgetStepConfigs[WidgetStep.ReviewStars].getSettings() as WidgetReviewFlowScreen
      return settings?.required ?? WidgetReviewQuestionFields[WidgetStep.ReviewStars].defaultRequired
    },
    getSettings<T extends WidgetReviewFlowScreen>(): T | undefined {
      return mergeSettings<WidgetReviewFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.stars,
        getWidgetSettings()?.reviewSettings?.screenSettings?.stars
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.ReviewStars),
    secondaryButton: createCancelReviewButton(),
  },
  [WidgetStep.ReviewEffects]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => (getReviewForm()?.effects?.length ?? 0) > 0,
    isRequired: () => {
      const settings = WidgetStepConfigs[WidgetStep.ReviewEffects].getSettings() as WidgetReviewFlowScreen
      return settings?.required ?? WidgetReviewQuestionFields[WidgetStep.ReviewEffects].defaultRequired
    },
    getSettings<T extends WidgetReviewFlowScreen>(): T | undefined {
      return mergeSettings<WidgetReviewFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.reviewEffects,
        getWidgetSettings()?.reviewSettings?.screenSettings?.effects
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.ReviewEffects),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.ReviewEffects),
  },
  [WidgetStep.ReviewPairedGoodWith]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => (getReviewForm()?.positiveExperiences?.length ?? 0) > 0,
    isRequired: () => {
      const settings = WidgetStepConfigs[
        WidgetStep.ReviewPairedGoodWith
      ].getSettings() as WidgetReviewFlowScreen
      return settings?.required ?? WidgetReviewQuestionFields[WidgetStep.ReviewPairedGoodWith].defaultRequired
    },
    getSettings<T extends WidgetReviewFlowScreen>(): T | undefined {
      return mergeSettings<WidgetReviewFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.reviewGoodExperience,
        getWidgetSettings()?.reviewSettings?.screenSettings?.goodExperience
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.ReviewPairedGoodWith),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.ReviewPairedGoodWith),
  },
  [WidgetStep.ReviewPairedBadWith]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => (getReviewForm()?.negativeExperiences?.length ?? 0) > 0,
    isRequired: () => {
      const settings = WidgetStepConfigs[
        WidgetStep.ReviewPairedBadWith
      ].getSettings() as WidgetReviewFlowScreen
      return settings?.required ?? WidgetReviewQuestionFields[WidgetStep.ReviewPairedBadWith].defaultRequired
    },
    getSettings<T extends WidgetReviewFlowScreen>(): T | undefined {
      return mergeSettings<WidgetReviewFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.reviewBadExperience,
        getWidgetSettings()?.reviewSettings?.screenSettings?.badExperience
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.ReviewPairedBadWith),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.ReviewPairedBadWith),
  },
  [WidgetStep.ReviewPotency]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => getReviewForm()?.potency !== undefined,
    isRequired: () => {
      const settings = WidgetStepConfigs[WidgetStep.ReviewPotency].getSettings() as WidgetReviewFlowScreen
      return settings?.required ?? WidgetReviewQuestionFields[WidgetStep.ReviewPotency].defaultRequired
    },
    getSettings<T extends WidgetReviewFlowScreen>(): T | undefined {
      return mergeSettings<WidgetReviewFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.reviewPotency,
        getWidgetSettings()?.reviewSettings?.screenSettings?.potency
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.ReviewPotency),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.ReviewPotency),
  },
  [WidgetStep.ReviewComments]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => (getReviewForm()?.reviewComment?.length ?? 0) > 0,
    isRequired: () => {
      const settings = WidgetStepConfigs[WidgetStep.ReviewComments].getSettings() as WidgetReviewFlowScreen
      return settings?.required ?? WidgetReviewQuestionFields[WidgetStep.ReviewComments].defaultRequired
    },
    getSettings<T extends WidgetReviewFlowScreen>(): T | undefined {
      return mergeSettings<WidgetReviewFlowScreen>(
        DEFAULT_SCREEN_SETTINGS.reviewComments,
        getWidgetSettings()?.reviewSettings?.screenSettings?.comment
      ) as T
    },
    primaryButton: createNextButton(WidgetStep.ReviewComments),
    secondaryButton: createSkipButtonIfOptional(WidgetStep.ReviewComments),
  },
  [WidgetStep.ReviewClaimPoints]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => true,
    isRequired: () => true,
    getSettings<T extends WidgetScreen>(): T | undefined {
      const settings = mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.reviewClaimPoints,
        getWidgetSettings()?.reviewSettings?.screenSettings?.claimPoints
      ) as T
      const pointsAwarded = useSubmitReviewStore.getState().response?.pointsAwarded
      settings.title =
        pointsAwarded != null
          ? settings.title?.replace('{points}', String(pointsAwarded))
          : settings.title?.replace('{points}', '').replace(/\s+/g, ' ').trim()
      return settings
    },
    primaryButton: () => {
      const { loading, success, error, response } = useSubmitReviewStore.getState()
      const pointsAwarded = response?.pointsAwarded ?? 0
      if (success && pointsAwarded > 0) {
        return { label: 'Next' }
      }
      if (!loading && !success) {
        return {
          label: error ? 'Try again' : 'Submit review',
          onClick: () => {
            void useSubmitReviewStore.getState().submitReview()
          },
        }
      }
      return undefined
    },
  },
  [WidgetStep.ReviewComplete]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => true,
    isRequired: () => true,
    getSettings<T extends WidgetScreen>(): T | undefined {
      return mergeSettings<WidgetScreen>(
        DEFAULT_SCREEN_SETTINGS.reviewComplete,
        getWidgetSettings()?.reviewSettings?.screenSettings?.complete
      ) as T
    },
    primaryButton: () => ({
      label: 'My reviews',
      onClick: () => {
        cancelReviewFlow()
      },
    }),
    secondaryButton: () => ({
      label: 'My recommendations',
      onClick: () => {
        useFlowState.getState().setCurrentFlow(WidgetFlow.Recommendation)
        useRecommendationStore.getState().resetRequest()
      },
    }),
  },
  [WidgetStep.ReviewMyReviews]: {
    flow: WidgetFlow.ProductReview,
    isComplete: () => true,
    isRequired: () => true,
    getSettings<T extends WidgetReviewMyReviewsScreenSettings>(): T | undefined {
      return mergeSettings<WidgetReviewMyReviewsScreenSettings>(
        DEFAULT_SCREEN_SETTINGS.reviewMyReviews,
        getWidgetSettings()?.reviewSettings?.screenSettings?.myReviews
      ) as T
    },
  },
  [WidgetStep.ReviewProductList]: {
    flow: undefined,
    isComplete: () => true,
    isRequired: () => true,
    backPlacement: 'replace-hamburger-menu',
    getSettings<T extends WidgetScreen>(): T | undefined {
      return mergeSettings<WidgetScreen>(
        {},
        getWidgetSettings()?.reviewSettings?.screenSettings?.productList
      ) as T
    },
  },
}

// =============================================================================
// Step Configs - Chat Flow
// =============================================================================

const ChatFlowStepConfigs: Partial<Record<WidgetStep, WidgetStepConfig>> = {
  [WidgetStep.Chat]: {
    flow: WidgetFlow.Chat,
    isComplete: () => true,
    isRequired: () => true,
    // Keep the hamburger so recent chats / location live in one menu.
    // Cross-flow switching uses header CTAs when recommendations are also on.
    cannotGoBack: () => true,
    getSettings<T extends WidgetChatScreen>(): T | undefined {
      return resolveChatScreenSettings(
        getWidgetSettings()?.chatSettings?.screenSettings?.chat
      ) as T
    },
  },
}

// =============================================================================
// Combined Step Configs Export
// =============================================================================

export const WidgetStepConfigs: Record<WidgetStep, WidgetStepConfig> = {
  ...GlobalStepConfigs,
  ...RecommendationFlowStepConfigs,
  ...ReviewFlowStepConfigs,
  ...ChatFlowStepConfigs,
} as Record<WidgetStep, WidgetStepConfig>

export type AuthStep = WidgetStep.SignInPage | WidgetStep.RecSignInPage | WidgetStep.ReviewSignInPage

const authSteps: AuthStep[] = [WidgetStep.SignInPage, WidgetStep.RecSignInPage, WidgetStep.ReviewSignInPage]

/**
 * Checks if the step is an authentication page
 * @param step - The step to check
 * @returns True if the step is an authentication page, false otherwise
 */
export function isAuthPage(step: WidgetStep): boolean {
  return authSteps.includes(step as AuthStep)
}
